import { GetUserData } from '@/constants/getters';
import { ChangeActiveOrganization, FetchBillingPortalUrl, FetchUserData } from '@/constants/actions';
import {
  daysUntilTrialEnd,
  sortByNewest,
  subscriptionUpdatedMessage,
  subscriptionTrialWillEndMessage,
} from '@/services/realtime-service/subscriptions/util';
import { Routes } from '@/constants/routes';

export default (ctx) => {
  return {
    channel: 'notifications',
    topic: 'toast',
    cb: async (message) => {
      const user = ctx.store.getters[`authStore/${GetUserData}`];

      if (!Array.isArray(message.data)) {
        message.data = [message.data];
      }
      message.data.sort(sortByNewest);

      for (const msg of message.data) {
        const { notificationId, notificationType, notification, createdAt, readAt } = msg;

        const { userId, userEmail, organizationName, trialEnd, plans } = notification;

        let event;
        const isCurrentUser = user.id === userId;

        switch (notificationType) {
          /**
           * A member was added to the current organization.
           */
          case 'addToOrganization': {
            event = {
              id: notificationId,
              description: `${userEmail} was added to ${organizationName}`,
              createdAt,
              readAt,
            };
            if (!isCurrentUser) {
              ctx.$notification.toast(event);
              ctx.$notification.bell(event);
            }
            break;
          }
          /**
           * A member was removed from the current organization.
           */
          case 'removeFromOrganization': {
            event = {
              id: notificationId,
              description: `${userEmail} was removed from ${organizationName}`,
              createdAt,
              readAt,
            };
            if (isCurrentUser) {
              event.description = `You have been removed from ${organizationName}`;
              ctx.$notification.toast(event);

              await ctx.store.dispatch(`authStore/${FetchUserData}`);
              await ctx.store.dispatch(`authStore/${ChangeActiveOrganization}`, {});

              ctx.error({ statusCode: 403.1, organizationName });
            } else {
              ctx.$notification.toast(event);
              ctx.$notification.bell(event);
            }
            break;
          }
          /**
           * A member joined (signed-up) to the current organization.
           */
          case 'joinOrganization': {
            event = {
              id: notificationId,
              description: `${userEmail} joined ${organizationName}`,
              createdAt,
              readAt,
            };
            ctx.$notification.toast(event);
            ctx.$notification.bell(event);
            break;
          }
          /**
           * Billing plan (subscription) has been updated.
           */
          case 'subscriptionUpdated': {
            event = {
              id: notificationId,
              description: subscriptionUpdatedMessage(plans),
              createdAt,
              readAt,
            };
            ctx.$notification.toast(event);
            ctx.$notification.bell(event);
            break;
          }
          /**
           * Payment error.
           */
          case 'subscriptionPaymentFailed': {
            event = {
              id: notificationId,
              description: `There seems to be an issue with your payment. Please check your credit card information.`,
              createdAt,
              readAt,
              closable: true,
              duration: '-1',
              actions: [
                {
                  text: 'Go to Billing Center',
                  onClick: async () => {
                    const externalUrl = await ctx.store.dispatch(`authStore/${FetchBillingPortalUrl}`, {
                      returnUrl: window.location.href,
                    });
                    if (externalUrl) {
                      window.location.href = externalUrl;
                    }
                  },
                },
              ],
            };
            ctx.$notification.toast(event);
            ctx.$notification.bell(event);
            break;
          }
          /**
           * Billing plan (subscription) trial will end.
           */
          case 'subscriptionTrialWillEnd': {
            const days = daysUntilTrialEnd(trialEnd);
            event = {
              id: notificationId,
              description: subscriptionTrialWillEndMessage(plans, days),
              createdAt,
              readAt,
              actions: [
                {
                  text: 'Upgrade now',
                  onClick: () => ctx.redirect(Routes.Plans),
                },
              ],
            };
            ctx.$notification.toast(event);
            ctx.$notification.bell(event);
            break;
          }
        }
      }
    },
  };
};
